// Phase 8.1 — Reusable Media Uploader
// =====================================
// Wraps the native /api/uploads endpoint with drag-and-drop, multi-file
// support, preview, remove-before-save and primary-image selection.
//
// Used by:
//   • Admin Catalog / Product Editor
//   • Partner Product Editor
//   • Franchise Product Editor
//   • Lifted Network post composer
//   • Training module editor
//
// Storage:
//   - Backed by /api/uploads (Mongo, base64). When S3/Cloudinary land the
//     storage backend swaps without touching this component.
//   - Each accepted file becomes { id, url, content_type, size_bytes }.
//
// Props:
//   value         array of { id, url, content_type, primary?, original_url? }
//   onChange      (next) => void
//   purpose       upload purpose tag (network_post, product_image, …)
//   accept        comma-separated mime list (defaults to images+mp4)
//   maxFiles      hard cap (default 12)
//   allowVideo    boolean (default true)
//   primaryEnabled show the "set primary" radio (default true for products)

import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, X, Star, Loader2, FileVideo, Image as ImageIcon } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const DEFAULT_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime";

export default function MediaUploader({
  value = [],
  onChange,
  purpose = "product_image",
  accept = DEFAULT_ACCEPT,
  maxFiles = 12,
  allowVideo = true,
  primaryEnabled = true,
  testId = "media-uploader",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [drag, setDrag] = useState(false);

  const upload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true); setErr("");
    const next = [...value];
    try {
      for (const file of Array.from(files)) {
        if (next.length >= maxFiles) {
          setErr(`Maximum ${maxFiles} files reached.`);
          break;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", purpose);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({
          id: data.id,
          url: data.url,
          content_type: data.content_type,
          size_bytes: data.size_bytes,
          primary: false,
          filename: file.name,
        });
      }
      // If no item is marked primary, mark the first image
      if (primaryEnabled && next.length > 0 && !next.some((m) => m.primary)) {
        const firstImage = next.find((m) => !m.content_type?.startsWith("video/")) || next[0];
        firstImage.primary = true;
      }
      onChange?.(next);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [value, onChange, purpose, maxFiles, primaryEnabled]);

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    // If we removed the primary, promote the first remaining image
    if (primaryEnabled && next.length > 0 && !next.some((m) => m.primary)) {
      const firstImage = next.find((m) => !m.content_type?.startsWith("video/")) || next[0];
      firstImage.primary = true;
    }
    onChange?.(next);
  };

  const setPrimary = (idx) => {
    onChange?.(value.map((m, i) => ({ ...m, primary: i === idx })));
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDrag(false);
    upload(e.dataTransfer?.files);
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition ${
          drag ? "border-lifted-green/60 bg-lifted-green/5"
               : "border-white/10 hover:border-lifted-green/40 hover:bg-white/[0.02]"
        }`}
        data-testid={`${testId}-dropzone`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid={`${testId}-input`}
        />
        {busy ? (
          <Loader2 className="h-6 w-6 text-lifted-green mx-auto animate-spin" />
        ) : (
          <UploadCloud className="h-6 w-6 text-lifted-green mx-auto" />
        )}
        <div className="font-bebas tracking-[0.2em] text-xs text-white mt-2">
          {busy ? "UPLOADING…" : "DROP FILES OR CLICK TO BROWSE"}
        </div>
        <div className="text-[0.65rem] text-lifted-dim mt-1">
          PNG · JPG · WEBP{allowVideo ? " · MP4" : ""} · max {maxFiles} files
        </div>
      </div>

      {err && (
        <div className="text-xs text-rose-300" data-testid={`${testId}-error`}>{err}</div>
      )}

      {/* Previews */}
      {value.length > 0 && (
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testId}-previews`}>
          {value.map((m, idx) => {
            const isVideo = m.content_type?.startsWith("video/");
            return (
              <li
                key={m.id || m.url || idx}
                className={`relative group rounded-lg overflow-hidden border ${
                  m.primary ? "border-lifted-green/60" : "border-white/10"
                }`}
                data-testid={`${testId}-item-${idx}`}
              >
                <div className="aspect-square bg-black/50 grid place-items-center">
                  {isVideo ? (
                    <video src={m.url} className="w-full h-full object-cover" muted playsInline />
                  ) : (
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                {/* Type icon */}
                <span className="absolute top-1.5 left-1.5 bg-black/70 text-lifted-text rounded px-1.5 py-0.5 text-[0.55rem] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                  {isVideo ? <FileVideo size={10} /> : <ImageIcon size={10} />}
                  {isVideo ? "MP4" : (m.content_type?.split("/")[1] || "IMG")}
                </span>
                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/70 hover:bg-rose-500/80 text-white grid place-items-center"
                  aria-label="Remove"
                  data-testid={`${testId}-remove-${idx}`}
                >
                  <X size={12} />
                </button>
                {/* Primary toggle */}
                {primaryEnabled && !isVideo && (
                  <button
                    type="button"
                    onClick={() => setPrimary(idx)}
                    className={`absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[0.55rem] font-bebas tracking-[0.16em] uppercase ${
                      m.primary
                        ? "bg-lifted-green text-black"
                        : "bg-black/70 text-white hover:bg-lifted-green/30"
                    }`}
                    data-testid={`${testId}-primary-${idx}`}
                  >
                    <Star size={10} className={m.primary ? "fill-current" : ""} />
                    {m.primary ? "Primary" : "Set primary"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
